import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  code: '',
  name: '',
  category: 'Cameras',
  location: 'Fort Worth, TX',
  totalQty: 1,
  image: '',
  qrCode: '',
  mediaCapacity: '',
  mediaFormat: '',
};

export default function AddItemDialog({ open, onClose, editingItem = null }) {
  const { addItem, updateItem, items, categories: CATEGORIES } = useApp();
  const isEdit = !!editingItem;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // Suggest the next available code: first gap in the WC item sequence
  // (matching the existing width-4 convention), falling back to max + 1.
  // Items use WC0xxx; kits use WC1xxx — keep the suggestion inside the
  // 0xxx item range so it never collides with the kit sequence.
  const suggestedCode = useMemo(() => {
    if (isEdit) return null;
    const PREFIX = 'WC';
    const WIDTH = 4;
    const nums = items
      .map((i) => {
        const m = /^WC(\d+)$/.exec(i.code || '');
        if (!m) return null;
        const n = parseInt(m[1], 10);
        return n < 1000 ? n : null;
      })
      .filter((n) => n !== null);
    if (nums.length === 0) return `${PREFIX}0001`;
    const used = new Set(nums);
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    for (let n = min; n <= max; n++) {
      if (!used.has(n)) return `${PREFIX}${String(n).padStart(WIDTH, '0')}`;
    }
    return `${PREFIX}${String(max + 1).padStart(WIDTH, '0')}`;
  }, [items, isEdit]);

  useEffect(() => {
    if (editingItem) {
      setForm({
        code: editingItem.code,
        name: editingItem.name,
        category: editingItem.category,
        location: editingItem.location || 'Fort Worth, TX',
        totalQty: editingItem.totalQty,
        image: editingItem.image || '',
        qrCode: editingItem.qrCode || editingItem.code,
        mediaCapacity: editingItem.mediaCapacity || '',
        mediaFormat: editingItem.mediaFormat || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [editingItem, open]);

  if (!open) return null;

  const onField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toUpperCase();
    if (!code) return toast.error('Code is required');
    if (!form.name.trim()) return toast.error('Name is required');
    if (!isEdit && items.some((i) => i.code === code))
      return toast.error(`Code ${code} already exists`);

    setSaving(true);
    try {
      if (isEdit) {
        await updateItem(editingItem.code, {
          code,
          name: form.name.trim(),
          category: form.category,
          location: form.location.trim(),
          totalQty: Number(form.totalQty) || 1,
          image: form.image.trim(),
          qrCode: form.qrCode.trim() || code,
          mediaCapacity: form.category === 'Media' ? form.mediaCapacity.trim() || null : null,
          mediaFormat: form.category === 'Media' ? form.mediaFormat.trim() || null : null,
        });
        toast.success(`${form.name} updated`);
      } else {
        await addItem({
          code,
          name: form.name.trim(),
          category: form.category,
          location: form.location.trim(),
          totalQty: Number(form.totalQty) || 1,
          image: form.image.trim(),
          qrCode: form.qrCode.trim() || code,
          mediaCapacity: form.category === 'Media' ? form.mediaCapacity.trim() || null : null,
          mediaFormat: form.category === 'Media' ? form.mediaFormat.trim() || null : null,
        });
        toast.success(`${form.name} added`);
      }
      onClose();
    } catch (err) {
      toast.error(err.message || 'Could not save item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="surface rounded-lg w-full max-w-lg overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
          <h2 className="text-base font-semibold text-white">{isEdit ? 'Edit Item' : 'Add Item'}</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Code *
              </label>
              <div className="relative">
                <input
                  autoFocus
                  value={form.code}
                  onChange={onField('code')}
                  placeholder="WC0200"
                  className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md pl-3 pr-[88px] py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                  data-testid="add-item-code-input"
                />
                {!isEdit && suggestedCode && (
                  <button
                    type="button"
                    onClick={() => {
                      setForm((f) => ({
                        ...f,
                        code: suggestedCode,
                        qrCode: f.qrCode ? f.qrCode : suggestedCode,
                      }));
                      toast.success(`Suggested ${suggestedCode}`);
                    }}
                    title={`Use next available code (${suggestedCode})`}
                    className="absolute inset-y-1 right-1 px-2 rounded text-[10px] tracking-wider uppercase font-semibold bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 inline-flex items-center gap-1"
                    data-testid="add-item-next-gap-btn"
                  >
                    <Sparkles size={11} /> Next
                  </button>
                )}
              </div>
              {!isEdit && suggestedCode && (
                <div className="text-[10px] text-neutral-500 mt-1">
                  Next available:{' '}
                  <span className="font-mono text-amber-300">{suggestedCode}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Quantity
              </label>
              <input
                type="number"
                min={1}
                value={form.totalQty}
                onChange={onField('totalQty')}
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Name *
            </label>
            <input
              value={form.name}
              onChange={onField('name')}
              placeholder="Canon EOS R5"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Category
              </label>
              <select
                value={form.category}
                onChange={onField('category')}
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Location
              </label>
              <input
                value={form.location}
                onChange={onField('location')}
                placeholder="Fort Worth, TX"
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                QR / Scan Code
              </label>
              <input
                value={form.qrCode}
                onChange={onField('qrCode')}
                placeholder="Same as code if blank"
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Image URL
              </label>
              <input
                value={form.image}
                onChange={onField('image')}
                placeholder="https://…"
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
          </div>

          {form.category === 'Media' && (
            <div
              className="grid grid-cols-2 gap-3 p-3 rounded-md border border-amber-500/20 bg-amber-500/5"
              data-testid="add-item-media-fields"
            >
              <div>
                <label className="block text-[10px] tracking-wider uppercase text-amber-300 mb-1.5">
                  Capacity
                </label>
                <input
                  value={form.mediaCapacity}
                  onChange={onField('mediaCapacity')}
                  placeholder="e.g. 512 GB"
                  className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-wider uppercase text-amber-300 mb-1.5">
                  Format
                </label>
                <input
                  value={form.mediaFormat}
                  onChange={onField('mediaFormat')}
                  placeholder="e.g. CFexpress, SD, SSD"
                  className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                />
              </div>
              <div className="col-span-2 text-[10px] text-amber-400/70 leading-relaxed">
                Media items start as <strong>Ready</strong>. Track their offload
                lifecycle on the <strong>Media Management</strong> page.
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-neutral-900 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
          >
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-amber text-xs px-4 py-1.5 rounded-md disabled:opacity-60">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </form>
    </div>
  );
}
